import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import NavigationMenu, { NavigationLogo, NavigationMenuGroup } from '../NavigationMenu/NavigationMenu';
import NavigationMenuTree from '../NavigationMenuTree/NavigationMenuTree';
import NavigationLeafList from '../NavigationLeafList/NavigationLeafList';
import MenuManagementModal from '../MenuManagementModal/MenuManagementModal';
import NavigationModeSwitch from '../../molecules/NavigationModeSwitch/NavigationModeSwitch';
import NavigationSearch from '../../molecules/NavigationSearch/NavigationSearch';
import Avatar from '../../atoms/Avatar/Avatar';
import NavigationTouchBackdrop from '../../atoms/NavigationTouchBackdrop/NavigationTouchBackdrop';

// Domain
import useMenu from '../../../domain/menu/useMenu';
import useMenuManagement from '../../../domain/menu/useMenuManagement';
import { removeMenuFavorite, splitMenuSections, toggleMenuFavorite } from '../../../domain/menu/menuManagementUseCase';

// Utils
import cn from '../../../utils/cn';

// Assets
import logo from '../../../assets/logo.svg';
import logoWhite from '../../../assets/logo-white.svg';

// Style
import styles from './NavigationSidebar.module.css';

const MANAGEMENT_OPTION_ID = 'management';
const SEARCH_OPTION_ID = 'search';

/**
 * NavigationSidebar component - App sidebar: modes (favorites / menu / management / search),
 * menu tree with popovers, bottom section and menu management modal.
 * @param {object} params
 * @param {ApiManagerUser | undefined} params.user - Current user
 * @param {ApiManagerMenu | undefined} params.menu - Menu from GET /layout
 * @param {boolean} params.isLoading - Whether the layout is loading
 * @param {(
 *  sections: { items: ApiManagerMenuItem[], bottomSectionItems: ApiManagerMenuItem[] },
 *  options?: { onSuccess?: () => void, optimistic?: boolean },
 * ) => void} params.onSaveMenu - Save menu configuration
 * @param {boolean} [params.isSavingMenu=false] - Whether the menu save is pending
 * @returns {JSX.Element} NavigationSidebar component
 */
function NavigationSidebar({
  user,
  menu,
  isLoading,
  onSaveMenu,
  isSavingMenu = false,
}) {
  const { t } = useTranslation();
  const searchInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [shouldFocusSearch, setShouldFocusSearch] = useState(false);
  const management = useMenuManagement({ items: menu?.items, bottomSectionItems: menu?.bottomSection?.items });
  const {
    mode,
    handleModeChange,
    search,
    setSearch,
    navIsOpen,
    handleNavMouseEnter,
    handleNavMouseLeave,
    expandedItemId,
    handleRootItemClick,
    popoverItem,
    popoverHiddenCount,
    handleSubItemClick,
    closePopover,
    navigateToItemId,
    visibleItems,
    bottomSectionItems,
    favoriteLeaves,
    searchLeaves,
  } = useMenu({ menu, isMenuLoading: isLoading, keepOpen: management.isOpen });

  /** @type {import('../../molecules/NavigationModeSwitch/NavigationModeSwitch').NavigationModeOption[]} */
  const modeOptions = [
    {
      id: 'favorites', label: t('menu.modes.favorites'), icon: 'star', showLabel: true,
    },
    {
      id: 'menu', label: t('menu.modes.menu'), icon: 'home', showLabel: true,
    },
    { id: MANAGEMENT_OPTION_ID, label: t('menu.modes.management'), icon: 'setting-2' },
    {
      id: SEARCH_OPTION_ID, label: t('menu.modes.search'), icon: 'search-normal', showLabel: true,
    },
  ];

  const handleModeSelect = (/** @type {string} */ id) => {
    if (id === MANAGEMENT_OPTION_ID) {
      management.openManagement();
      return;
    }
    if (id === SEARCH_OPTION_ID) setShouldFocusSearch(true);
    handleModeChange(/** @type {import('../../../domain/menu/useMenu').MenuMode} */ (id));
  };

  // Runs after the commit that mounts the search field (mode + open state are set in the same batch)
  useEffect(() => {
    if (!shouldFocusSearch) return;
    searchInputRef.current?.focus();
    setShouldFocusSearch(false);
  }, [shouldFocusSearch]);

  const handleManageFromPopover = (/** @type {string} */ itemId) => {
    closePopover();
    management.openManagement(itemId);
  };

  // Favorites can live in both sections: edit the combined tree, then split it back
  const menuTree = [...(menu?.items ?? []), ...(menu?.bottomSection?.items ?? [])];
  const toMenuSections = (/** @type {ApiManagerMenuItem[]} */ tree) => (
    splitMenuSections(tree, menu?.bottomSection?.items)
  );

  const handleRemoveFavorite = (/** @type {string} */ itemId) => {
    onSaveMenu(toMenuSections(removeMenuFavorite(menuTree, itemId)), { optimistic: true });
  };

  const handleToggleFavorite = (/** @type {string} */ itemId) => {
    onSaveMenu(toMenuSections(toggleMenuFavorite(menuTree, itemId)), { optimistic: true });
  };

  const handleValidateManagement = () => {
    onSaveMenu(toMenuSections(management.draft), { onSuccess: management.closeManagement });
  };

  return (
    <div
      className={cn([styles.navigation, navIsOpen && styles['navigation-is-open']])}
      onMouseEnter={handleNavMouseEnter}
      onMouseLeave={handleNavMouseLeave}
    >
      <NavigationTouchBackdrop isDisplayed={!navIsOpen} onClick={handleNavMouseEnter} />
      <NavigationMenu className={styles['navigation-menu']} isOpen={navIsOpen}>
        <NavigationMenuGroup gap="s">
          <div className={styles.header}>
            <Avatar
              src={user?.avatar}
              alt="Avatar"
              isMenuOpen={navIsOpen}
              notificationCount={user?.notificationCount}
              // eslint-disable-next-line no-console
              onClick={() => console.log('Avatar clicked')}
              isLoading={isLoading}
            />
          </div>

          <NavigationModeSwitch
            options={modeOptions}
            activeId={mode}
            onSelect={handleModeSelect}
            isMenuOpen={navIsOpen}
          />

          {mode === 'search' && navIsOpen ? (
            <NavigationSearch
              ref={searchInputRef}
              id="navigation-menu-search"
              theme="form"
              className={styles.search}
              onFocus={handleNavMouseEnter}
              placeholder={t('menu.search.placeholder')}
              isMenuOpen={navIsOpen}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
              isLoading={isLoading}
            />
          ) : null}

          {mode === 'menu' ? (
            <NavigationMenuTree
              items={visibleItems}
              isMenuOpen={navIsOpen}
              expandedItemId={expandedItemId}
              popoverItem={popoverItem}
              popoverHiddenCount={popoverHiddenCount}
              onRootItemClick={handleRootItemClick}
              onSubItemClick={handleSubItemClick}
              onPopoverClose={closePopover}
              onLinkClick={navigateToItemId}
              onManageClick={handleManageFromPopover}
              onFavoriteToggle={handleToggleFavorite}
            />
          ) : null}

          {mode === 'favorites' ? (
            <NavigationLeafList
              leaves={favoriteLeaves}
              onLeafClick={navigateToItemId}
              emptyLabel={t('menu.favorites.empty')}
              isMenuOpen={navIsOpen}
              action={{ icon: 'star-filled', label: t('menu.management.removeFromFavorites') }}
              onLeafAction={handleRemoveFavorite}
            />
          ) : null}

          {mode === 'search' && search.trim() ? (
            <NavigationLeafList
              leaves={searchLeaves}
              onLeafClick={navigateToItemId}
              emptyLabel={t('menu.search.empty')}
              isMenuOpen={navIsOpen}
              forbiddenLabel={t('menu.search.forbidden')}
            />
          ) : null}
        </NavigationMenuGroup>

        <div className={styles.footer}>
          {bottomSectionItems.length ? (
            <NavigationMenuTree
              items={bottomSectionItems}
              isMenuOpen={navIsOpen}
              expandedItemId={expandedItemId}
              popoverItem={popoverItem}
              popoverHiddenCount={popoverHiddenCount}
              onRootItemClick={handleRootItemClick}
              onSubItemClick={handleSubItemClick}
              onPopoverClose={closePopover}
              onLinkClick={navigateToItemId}
              onManageClick={handleManageFromPopover}
            />
          ) : null}
          <NavigationLogo>
            <img src={navIsOpen ? logo : logoWhite} alt="Logo" />
          </NavigationLogo>
        </div>
      </NavigationMenu>

      <MenuManagementModal
        isOpen={management.isOpen}
        items={management.configurableItems}
        focusItemId={management.focusItemId}
        onAvailableChange={management.toggleAvailable}
        onFavoriteChange={management.toggleFavorite}
        onValidate={handleValidateManagement}
        onCancel={management.closeManagement}
        isSaving={isSavingMenu}
      />
    </div>
  );
}

export default NavigationSidebar;
